import type { ReactNode } from "react";
import styled from "@emotion/styled";
import { Typography } from "@avenue-8/sidekick-ui";
import { LeadChip } from "./LeadChip";
import type { LeadChipItem } from "./LeadChip";
import { Avatar, ListItemButton } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const CardRow = styled(ListItemButton)`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  text-align: left;
  border: 1px solid transparent;
  background-color: ${(p) => p.theme.colors.background};
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
  box-shadow: none;

  &[data-padding="condensed"] {
    padding: 0.5rem 0.75rem;
  }

  &[data-padding="none"] {
    padding: 0;
  }

  &[data-shape="square"] {
    border-radius: 0;
  }

  &[data-variant="flat"] {
    background-color: transparent;
  }

  &[data-variant="card"]:hover {
    background-color: ${(p) => p.theme.colors.background5};
    box-shadow: none;
    border-color: ${(p) => p.theme.text.colors.brand};
  }
`;

const LeadAvatar = styled(Avatar)`
  width: 2.5rem;
  height: 2.5rem;
  border: 2px solid ${(p) => p.theme.text.colors.brand};
  background-color: ${(p) => p.theme.colors.background5};
  color: ${(p) => p.theme.text.colors.brand};
  font-size: ${(p) => p.theme.text.fontSizes.s};
  font-weight: 600;
  flex-shrink: 0;
`;

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const ChevronWrap = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.text.colors.secondary};
  flex-shrink: 0;
`;

type LeadCardProps = {
  title?: string;
  avatar?: ReactNode;
  chips?: LeadChipItem[];
  signal?: ReactNode;
  padding?: "default" | "condensed" | "none";
  variant?: "card" | "flat";
  shape?: "rounded" | "square";
  onClick?: () => void;
  disabled?: boolean;
};

export const LeadCard = ({
  title,
  avatar,
  chips,
  signal,
  padding = "default",
  variant = "card",
  shape,
  onClick,
  disabled,
}: LeadCardProps) => {
  return <CardRow
    onClick={onClick}
    disabled={disabled}
    data-padding={padding}
    data-variant={variant}
    data-shape={shape}
  >
    {avatar && <LeadAvatar>
      {avatar}
    </LeadAvatar>}
    <CardBody>
      {title && (
        <Typography
          variant="subtitle2"
          sx={{ color: "primary.title", textTransform: "capitalize" }}
        >
          {title}
        </Typography>
      )}
      {chips && chips.length > 0 && (
        <ChipsRow>
          {chips.map((chip) => (
            <LeadChip key={`${chip.label}-${chip.value}`} {...chip} />
          ))}
        </ChipsRow>
      )}
      {signal && <Typography variant="body2">{signal}</Typography>}
    </CardBody>
    {onClick && (
      <ChevronWrap>
        <ChevronRightIcon fontSize="small" aria-hidden />
      </ChevronWrap>
    )}
  </CardRow>
};
